





















































































































import { computed, defineComponent, PropType, ref, watch } from "@vue/composition-api";
import { debounce } from "lodash-es";
import { ContentFile, ContentFileThumbnailType } from "@scrile/api-provider/dist/api/ContentFilesProvider";
import { ContentSet, InputContentSetUpdate } from "@scrile/api-provider/dist/api/ContentSetProvider";
import { Participant } from "@scrile/api-provider/dist/api/ParticipantProvider";
import { SubscriptionLevel } from "@scrile/api-provider/dist/api/SubscriptionLevelProvider";
import { ContentFeedTypeEnum } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import { getElement } from "@/lib/elementHelpers";
import { isVideoFile } from "@/lib/fileHelpers";
import { $t } from "@/lib/i18nHelpers";
import config from "@/config";
import { PostFileData } from "@/types";
import BaseSpinner from "@/components/BaseSpinner";
import BaseStepper from "@/components/BaseStepper";
import ContentFeedPreview from "@/components/ContentFeedPreview";
import ModalView from "@/components/ModalView";
import ProgressStepsCard from "@/components/ui/ProgressStepsCard";
import AddMainVideo from "./components/AddMainVideo.vue";
import MainVideo from "./components/MainVideo.vue";
import MediaPostPriceSettings from "./components/MediaPostPriceSettings.vue";
import Participants from "./components/Participants.vue";
import PostThumbnails from "./components/PostThumbnails.vue";
import ShootingDetails from "./components/ShootingDetails.vue";
import TrailerVideo from "./components/TrailerVideo.vue";
import AllowCommentsToggle from "../../components/AllowCommentsToggle";
import InputWithMentions from "../../components/InputWithMetions";
import PostHashtagsSettings from "../../components/PostHashtagsSettings";
import SchedulePostSettings from "../../components/SchedulePostSettings";
import StepperButtons from "../../components/StepperButtons";
import useStepperData from "../../hooks/useStepperData";
import { FileUploadData, GenerateFileData, PostPriceData } from "../../types";

export default defineComponent({
  name: "MediaPostCreateView",
  components: {
    PostHashtagsSettings,
    MediaPostPriceSettings,
    Participants,
    ShootingDetails,
    ProgressStepsCard,
    AddMainVideo,
    PostThumbnails,
    TrailerVideo,
    MainVideo,
    AllowCommentsToggle,
    StepperButtons,
    ContentFeedPreview,
    SchedulePostSettings,
    BaseStepper,
    InputWithMentions,
    BaseSpinner,
    ModalView,
  },
  props: {
    show: { type: Boolean, default: false },
    loading: { type: Boolean, required: true },
    saving: { type: Boolean, default: false },
    postData: { type: Object as PropType<ContentSet | null> },
    fileList: { type: Array as PropType<PostFileData[]>, required: true },
    previewFiles: { type: Array as PropType<Array<ContentFile | FileUploadData>>, required: true },
    isDraft: { type: Boolean, default: false },
    generateFileData: { type: Array as PropType<GenerateFileData[]>, default: () => [] },
    participants: { type: Array as PropType<Participant[]>, default: () => [] },
  },
  setup(props, { emit }) {
    const hashtagsAvailable = computed(() => config.featureHashtags);
    const stepperEl = ref();
    const recurringLevel = ref<SubscriptionLevel | null>(null);
    const description = ref(props.postData?.description ?? "");
    const generateTrailer = ref(!!props.generateFileData.length);
    const trailerRange = ref<Pick<GenerateFileData, "startTime" | "finishTime">>({
      startTime: props.generateFileData[0]?.startTime ?? 0,
      finishTime: props.generateFileData[0]?.finishTime ?? 120000,
    });

    const hasParticipants = computed(() => props.participants.length > 0);

    const title = computed(() => (props.isDraft ? $t("New video") : $t("Edit video")));
    const allowManageReviews = computed(() => config.featureManageFeedReviews);
    const allowManageSchedule = computed(() => props.isDraft || !props.postData?.publishTime);

    const mainVideos = computed(() => props.fileList.filter(i => !i.public && isVideoFile(i)));
    const trailers = computed(() => props.fileList.filter(i => i.public && isVideoFile(i)));
    const sfwThumbnails = computed(() => props.fileList.filter(i => i.thumbnailType === ContentFileThumbnailType.SFW));
    const nsfwThumbnails = computed(() =>
      props.fileList.filter(i => i.thumbnailType === ContentFileThumbnailType.NSFW)
    );
    const canPublish = computed(
      () =>
        !!description.value &&
        mainVideos.value.length > 0 &&
        (trailers.value.length > 0 || generateTrailer.value) &&
        sfwThumbnails.value.length > 0 &&
        nsfwThumbnails.value.length > 0 &&
        hasParticipants.value
    );

    const isPostEmpty = computed(() => mainVideos.value.every(v => v.uploading));

    const priceData = computed<PostPriceData>(() => ({
      subscriptionLevelId: recurringLevel.value?.id ?? "0",
      pricePackageId: "0",
    }));

    const validationMap = computed(() => ({
      description: !!description.value,
      trailer: trailers.value.length > 0 || generateTrailer.value,
      sfwThumbnails: sfwThumbnails.value.length > 0,
      nsfwThumbnails: nsfwThumbnails.value.length > 0,
      participants: hasParticipants.value,
    }));

    const validationGoal = computed(() => Object.values(validationMap.value).length);
    const validationCurrent = computed(() => Object.values(validationMap.value).filter(i => i).length);

    const stepsArray = computed(() => ["files", "preview"]);

    const { currentStep, steps, getStepName } = useStepperData(stepsArray);

    const onPriceChanged = ({
      recurringLevel: updatedRecurringLevel,
    }: {
      recurringLevel: SubscriptionLevel | null;
    }) => {
      recurringLevel.value = updatedRecurringLevel;
    };

    const onPublish = () => {
      const data: Omit<InputContentSetUpdate, "id"> = {
        description: description.value,
        published: true,
        allowReviews: allowManageReviews.value ? props.postData?.allowReviews : undefined,
        tagIds: props.postData?.tagIds ?? [],
      };
      emit("update", { data, closeOnSave: true });
    };

    const onTrailerRangeChange = ({ startTime, finishTime }: Pick<GenerateFileData, "startTime" | "finishTime">) => {
      trailerRange.value = { startTime, finishTime };
    };

    const onTrailerRangeReset = () => {
      generateTrailer.value = false;
      trailerRange.value = { startTime: 0, finishTime: 120000 };
    };

    const onCurrentStepChange = () => {
      getElement<HTMLElement>(stepperEl.value).scrollIntoView({
        block: "start",
        behavior: "auto",
      });
    };

    watch(isPostEmpty, value => emit("update:isPostEmpty", value), { immediate: true });

    watch(mainVideos, (newVal, oldVal) => {
      if (newVal.length === 0 && oldVal.length > 0) {
        onTrailerRangeReset();
      }
    });

    watch(
      description,
      debounce((description: string) => {
        const data: Omit<InputContentSetUpdate, "id"> = {
          description,
        };
        emit("partialUpdate", { data });
      }, 500)
    );

    emit("setUpdateCb", () => {
      if (generateTrailer.value) {
        trailers.value.forEach(i => emit("fileRemove", i.id));
        emit("addRequestGenerateFile", {
          id: mainVideos.value[0].id,
          publicAccess: true,
          startTime: trailerRange.value.startTime,
          finishTime: trailerRange.value.finishTime,
        } as GenerateFileData);
      } else {
        props.generateFileData.forEach(i => emit("removeRequestGenerateFile", i.id));
      }
    });

    return {
      hashtagsAvailable,
      ContentFileThumbnailType,
      stepperEl,
      title,
      description,
      steps,
      allowManageReviews,
      allowManageSchedule,
      priceData,
      ContentFeedTypeEnum,
      currentStep,
      generateTrailer,
      mainVideos,
      trailers,
      sfwThumbnails,
      nsfwThumbnails,
      canPublish,
      trailerRange,
      validationGoal,
      validationCurrent,
      onCurrentStepChange,
      getStepName,
      onPriceChanged,
      onPublish,
      onTrailerRangeChange,
      onTrailerRangeReset,
    };
  },
});
